import { ChallengeService } from '@duck/api-definitions'
import {
  CommandLineIcon,
  ClockIcon,
  PlayIcon,
} from '@heroicons/react/24/outline'
import { TranslationKey, useTar, TranslateFunction } from '@/modules'
import { StandardLoader } from '../StandardLoader'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from '@tanstack/react-router'
import { apis } from '../../modules/apiClients'
import { useState } from 'react'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@duck/ui'
import { toast } from 'sonner'
import { RelativeDateTime } from '../datetime/RelativeDateTime'

interface Assignment extends ChallengeService.AvailableAssignment {
  icon?: React.ComponentType<{ className?: string }>
  estimatedTime?: string
}

interface AssignmentCardProps {
  assignment: Assignment
}

const actionButtonMap: Record<ChallengeService.AvailableChallengeStatusEnum, { text: TranslationKey, className: string }> = {
  [ChallengeService.AvailableChallengeStatusEnum.Available]: {
    text: 'challenges.startChallenge',
    className: 'bg-accent hover:bg-accent/80'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Unbooted]: {
    text: 'challenges.continue',
    className: 'bg-accent hover:bg-accent/80 ongoing-button-shadow'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Ongoing]: {
    text: 'challenges.continue',
    className: 'bg-accent hover:bg-accent/80 ongoing-button-shadow'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Cooldown]: {
    text: 'challenges.retakeInTIME',
    className: 'bg-accent/20 hover:bg-accent/30'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Finished]: {
    text: 'challenges.retake',
    className: 'bg-success hover:bg-success/80'
  },
  [ChallengeService.AvailableChallengeStatusEnum.Aborted]: {
    text: 'challenges.resume',
    className: 'bg-warning hover:bg-warning/80'
  }
}

const getTimeDifferenceText = (isoDateString: string, t: TranslateFunction): string => {
  const date = new Date(isoDateString)
  const now = new Date()
  const diffInMs = date.getTime() - now.getTime()

  if (diffInMs <= 0) return ''

  const diffInMinutes = Math.floor(diffInMs / 1000 / 60)
  const diffInHours = Math.floor(diffInMinutes / 60)
  const diffInDays = Math.floor(diffInHours / 24)

  if (diffInDays > 0) {
    return diffInDays === 1 ? t('times.oneDay') : t('times.xDays', { x: diffInDays })
  }
  if (diffInHours > 0) {
    return diffInHours === 1 ? t('times.oneHour') : t('times.xHours', { x: diffInHours })
  }
  if (diffInMinutes > 0) {
    return diffInMinutes === 1 ? t('times.oneMinute') : t('times.xMinutes', { x: diffInMinutes })
  }
  return t('times.verySoon')
}

const getActionButton = (
  status: ChallengeService.AvailableChallengeStatusEnum,
  startDate?: string | null,
  endDate?: string | null,
  t?: TranslateFunction
): {
  text: TranslationKey | string,
  className: string,
  disabled: boolean,
  isCustomText: boolean
} => {
  const now = new Date()

  // Check if due date is in the past
  if (endDate) {
    const dueDate = new Date(endDate)
    if (dueDate < now) {
      return {
        text: 'challenges.submissionClosed',
        className: 'bg-accent/20 hover:bg-accent/30',
        disabled: true,
        isCustomText: false
      }
    }
  }

  // Check if start date is in the future
  if (startDate) {
    const start = new Date(startDate)
    if (start > now && t) {
      const timeDiff = getTimeDifferenceText(startDate, t)
      return {
        text: t('challenges.startInX', { x: timeDiff }),
        className: 'bg-accent/20 hover:bg-accent/30',
        disabled: true,
        isCustomText: true
      }
    }
  }

  // Return normal button config based on status
  const config = actionButtonMap[status] || {
    text: 'challenges.startChallenge',
    className: 'bg-accent hover:bg-accent/80'
  }

  return {
    ...config,
    disabled: status === ChallengeService.AvailableChallengeStatusEnum.Cooldown,
    isCustomText: false
  }
}

const getTimeLimitDisplay = (timeLimitMinutes: number, t: TranslateFunction): string => {
  if (timeLimitMinutes < 60) {
    return timeLimitMinutes === 1 ? t('times.oneMinute') : t('times.xMinutes', { x: timeLimitMinutes })
  }
  if (timeLimitMinutes % 60 === 0) {
    const hours = timeLimitMinutes / 60
    return hours === 1 ? t('times.oneHour') : t('times.xHours', { x: hours })
  }
  return t('times.xMinutes', { x: timeLimitMinutes })
}

export const AssignmentCard: React.FC<AssignmentCardProps> = ({
  assignment,
}) => {
  const { t } = useTar()
  const { loggedInAction } = useAuth()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isTimeLimitDialogOpen, setIsTimeLimitDialogOpen] = useState(false)
  const IconComponent = assignment.icon || CommandLineIcon
  const actionButtonConfig = getActionButton(assignment.status, assignment.startDate, assignment.endDate, t)
  const hasTimeLimit = assignment.timeLimit != null && assignment.timeLimit > 0
  const isStartWithTimeLimit =
    assignment.status === ChallengeService.AvailableChallengeStatusEnum.Available && hasTimeLimit

  const doStart = () => {
    loggedInAction(async () => {
      setIsLoading(true)
      try {
        if (assignment.status === ChallengeService.AvailableChallengeStatusEnum.Available) {
          const resp = await apis.challengeService.bootEnvironment({
            taskId: assignment.taskId,
            assignmentId: assignment.assignmentId
          })
          const slug = resp.data.slug
          if (slug) {
            sessionStorage.setItem('challenge_unbooted', slug)
            navigate({ to: `/challenge/${slug}` })
            return
          }
        }
        if (assignment.slug && assignment.status === ChallengeService.AvailableChallengeStatusEnum.Unbooted) {
          sessionStorage.setItem('challenge_unbooted', assignment.slug)
          navigate({ to: `/challenge/${assignment.slug}` })
          return
        }
        if (assignment.slug && assignment.status === ChallengeService.AvailableChallengeStatusEnum.Ongoing) {
          navigate({ to: `/challenge/${assignment.slug}` })
          return
        }
      } catch {
        toast.error(t('challenges.failedToStartChallenge'), {
          duration: 10000
        })
      } finally {
        setIsLoading(false)
      }
    })
  }

  const handleActionClick = () => {
    if (isStartWithTimeLimit) {
      setIsTimeLimitDialogOpen(true)
      return
    }
    doStart()
  }

  const handleTimeLimitDialogConfirm = () => {
    setIsTimeLimitDialogOpen(false)
    doStart()
  }

  return (
    <div
      className="bg-card rounded-lg border border-border p-6 shadow-sm hover:shadow-lg transition-shadow duration-200 h-80 flex flex-col"
    >
      {/* Assignment Icon & Category */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-accent/10 rounded-lg">
            <IconComponent className="w-6 h-6 text-accent" />
          </div>
          <span className="text-xs font-medium text-muted uppercase tracking-wide">
            {assignment.language}
          </span>
        </div>

        {/* Organization Badge */}
        {assignment.organizationName && (
          <span className="px-2 py-1 text-xs font-medium rounded-full border bg-muted/10 text-muted-foreground border-muted/20">
            {assignment.organizationName}
          </span>
        )}
      </div>

      {/* Assignment Title */}
      <h2 className="text-xl font-semibold text-foreground mb-3 h-12 leading-6 flex items-start">
        <span className="line-clamp-2">
          {assignment.name}
        </span>
      </h2>

      {/* Assignment Dates & Status */}
      <div className="flex flex-col flex-1 gap-1 text-foreground/80 text-xs mb-6">
        {assignment.startDate && (
          <div className="flex items-center">
            <svg className="w-4 h-4 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{t('partner.startDate')}: </span>
            <RelativeDateTime
              isoDateString={assignment.startDate}
              className="text-sm font-semibold ms-1"
            />
          </div>
        )}
        {assignment.endDate && (
          <div className="flex items-center">
            <svg className="w-4 h-4 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{t('partner.dueDate')}: </span>
            <RelativeDateTime
              isoDateString={assignment.endDate}
              className="text-sm font-semibold ms-1"
            />
          </div>
        )}
        {hasTimeLimit && assignment.timeLimit != null && (
          <div className="flex items-center">
            <ClockIcon className="w-4 h-4 mr-1" />
            <span>{t('partner.timeWindow')}: </span>
            <span className="text-sm font-semibold ml-1">
              {t('challenges.xFromStart', { x: getTimeLimitDisplay(assignment.timeLimit, t) })}
            </span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          className={`flex-1 flex items-center justify-center gap-2 ${!isLoading ? actionButtonConfig.className : ''}`}
          variant={isLoading ? "outline" : "default"}
          onClick={handleActionClick}
          disabled={isLoading || actionButtonConfig.disabled}
        >
          {isLoading ? (
            <StandardLoader size="md" inline />
          ) : (
            <>
              <PlayIcon className="w-4 h-4" />
              <span>{actionButtonConfig.isCustomText ? actionButtonConfig.text : t(actionButtonConfig.text as TranslationKey)}</span>
            </>
          )}
        </Button>
      </div>
      <Dialog open={isTimeLimitDialogOpen} onOpenChange={setIsTimeLimitDialogOpen}>
        <DialogContent noMinHeight noCloseButton width="sm">
          <DialogHeader>
            <DialogTitle>{t('common.thisChallengeHasATimeLimit')}</DialogTitle>
          </DialogHeader>
          <DialogDescription asChild>
            <div className="flex flex-col gap-1 text-muted-foreground">
              <div>
                {t('common.timeLimitDescription', { timeLimit: getTimeLimitDisplay(assignment.timeLimit ?? 0, t) })}
              </div>
            </div>
          </DialogDescription>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsTimeLimitDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleTimeLimitDialogConfirm}>
              {t('common.start')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export type { Assignment, AssignmentCardProps }
